import { Build, CommandJob, Job, Step } from "~/components/build/Show/lib/types";
import { QueryClient, queryOptions, UseQueryOptions } from "@tanstack/react-query";
import fetcher from "~/components/build/Show/lib/fetcher";
import { stepQueryKey } from "~/components/build/Show/lib/queries/step";
import { stepJobsQueryKey } from "~/components/build/Show/lib/queries/stepJobs";
import { isMatrixCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import { groupBy } from "lodash";
import { jobDrawerQueryKey } from "~/components/build/Show/lib/queries/jobDrawer";

/**
 * TODO: Figure out if we can remove this query in favour of something like:
 * useQueries([
 *   stepQueryOptions(build, stepUuid), // Get the step
 *   stepJobsQueryOptions(build, stepUuid), // Get the jobs for a step
 * ])
 */

interface StepDrawerQueryResult {
  step: Step;
  jobs: Job[];
}

export const stepDrawerQueryKey = (build: Build, stepUuid: Step["uuid"]) => {
  return ["build", build.id, "steps", stepUuid, "step-drawer"];
};

export const stepDrawerQueryOptions = (
  build: Build,
  stepUuid: Step["uuid"],
  queryOptionsArgs: Omit<UseQueryOptions<StepDrawerQueryResult>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: stepDrawerQueryKey(build, stepUuid),
    queryFn: ({ client: queryClient }) =>
      fetchStepDrawer(build.buildDataBasePath, stepUuid).then((data) => {
        populateQueryCache(queryClient, data, build, stepUuid);

        return data;
      }),
    ...queryOptionsArgs,
  });
};

function populateQueryCache(
  queryClient: QueryClient,
  data: StepDrawerQueryResult,
  build: Build,
  stepUuid: Step["uuid"],
) {
  // Populate step information
  queryClient.setQueryData(stepQueryKey(build, stepUuid), data.step);

  // Populate step-jobs information
  queryClient.setQueryData(stepJobsQueryKey(build, stepUuid), data.jobs);

  // Populate job-drawer queries from results
  if (isParallelCommandStep(data.step)) {
    parallelStepJobsGroupedByParallelIndex(data.jobs as CommandJob[]).forEach((jobs) => {
      jobs.forEach((job: CommandJob) => {
        queryClient.setQueryData(jobDrawerQueryKey(build, job.id), {
          job: job,
          jobWithRetries: jobs,
        });
      });
    });
  } else if (isMatrixCommandStep(data.step)) {
    matrixStepJobsGroupedByMatrixDimension(data.jobs as CommandJob[]).forEach((jobs) => {
      jobs.forEach((job: CommandJob) => {
        queryClient.setQueryData(jobDrawerQueryKey(build, job.id), {
          job: job,
          jobWithRetries: jobs,
        });
      });
    });
  } else {
    data.jobs.forEach((job) => {
      queryClient.setQueryData(jobDrawerQueryKey(build, job.id), {
        job: job,
        jobWithRetries: data.jobs,
      });
    });
  }
}

function parallelStepJobsGroupedByParallelIndex(jobs: CommandJob[]): Array<CommandJob[]> {
  const jobsByParallelIndex = Object.values(
    groupBy(jobs, (job: CommandJob) => job.parallelGroupIndex),
  ) as Array<CommandJob[]>;
  return jobsByParallelIndex;
}

function matrixStepJobsGroupedByMatrixDimension(jobs: CommandJob[]): Array<CommandJob[]> {
  const jobsGroupedByMatrixDimension = Object.values(
    groupBy(jobs, (job: CommandJob) => JSON.stringify(job.matrix)),
  ) as Array<CommandJob[]>;
  return jobsGroupedByMatrixDimension;
}

function fetchStepDrawer(buildDataBasePath: Build["buildDataBasePath"], stepUuid: Step["uuid"]) {
  const url = new URL(
    `${buildDataBasePath}/steps/${stepUuid}?include_jobs=true`,
    window.location.origin,
  );

  return fetcher<StepDrawerQueryResult>(url);
}
